import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMutation } from 'react-query'
import jwtDecode from 'jwt-decode';
import { useForm } from 'react-hook-form';
import { Form, Input, Label, Button, Spinner, Card, FormGroup, FormFeedback } from 'reactstrap';
import { toast } from 'components/Shared/Toast';
import clsx from 'clsx';

import { useDomainProperties } from 'hooks/useDomainProperties';
import AuthLayout from 'layouts/AuthLayout';
import bootbox from 'components/Modals/Bootbox';
import { useAuth } from 'context/auth';
import { redirectTo } from 'utils';
import { login, respondChallenge, forgotPassword } from 'api/actions/auth'
import { fetcher } from 'api/client';
import useQueryParams from 'hooks/useQueryParams';

function parseAuthResult(data) {
    if (!data) return {};
    if (!data.AuthenticationResult) return data;

    const authResult = data.AuthenticationResult
    const idToken = jwtDecode(authResult.IdToken);


    return {
        accessToken: authResult.AccessToken,
        refreshToken: authResult.RefreshToken,
        idToken: authResult.IdToken,
        expiresIn: authResult.ExpiresIn,
        expiresAt: Date.now() + (Number(authResult.ExpiresIn) * 1000),
        user: {
            _id: idToken.profile,
            givenName: idToken.given_name,
            familyName: idToken.family_name,
            name: idToken.name,
            email: idToken.email,
            verified: idToken.email_verified,
            cognitoId: idToken.sub,
            //     organization: idToken['custom:organization']
        },
    }
}

function LoginForm({ isSlack }) {

    const { t, i18n } = useTranslation();
    const history = useHistory();
    const { register, handleSubmit, getValues, errors } = useForm();
    const { signin } = useAuth();
    const [, setToken] = useState(null);
    const [twoFactor, setTwoFactor] = useState(null);
    const location = useLocation();
    const queryParams = useQueryParams();
    const sessionId = queryParams.get('sessionId')
      
    const { from } = location.state || { from: { pathname: '/dashboard' } };


    const onSuccess = ({ data:result }) => {
         const data = parseAuthResult(result);
        if (data.hasChallenge && data.challengeName === 'NEW_PASSWORD_REQUIRED') {
            history.push(`/account/${data.challengeParameters.username}/change-password/${data.challengeParameters.session}?hasTmpPwd=true`)
        } else if (data.hasChallenge && data.challengeName === 'SOFTWARE_TOKEN_MFA') {
            if (data.shouldRedirect) {
                window.location.href = `https://${data.domain}.trato.io/login?session=${data.challengeParameters.session}&username=${data.challengeParameters.userIdForSRP}&hasInitialConfig=${data.hasInitialConfig}&email=${getValues('email')}&type=two-factor-redirect`
                return;
            }
            setTwoFactor({
                session: data.challengeParameters.session,
                username: data.challengeParameters.userIdForSRP,
                hasInitialConfig: data.hasInitialConfig
            })
        } else if (data.hasChallenge && data.challengeName === 'REDIRECT_CHALLENGE') {
            redirectTo(data.domain, data.challengeParameters.session, data.challengeParameters.username)
        } else if (data.twofactorToken) {
            setToken(data.twofactorToken);
        } else if (data.accessToken) {
            const auth = {
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                idToken: data.idToken,
                expiresIn: data.expiresIn,
                expiresAt: Date.now() + (Number(data.expiresIn) * 1000),
                user: data.user
            }

            setTwoFactor(null)
            i18n.changeLanguage(data.user?.language ?? i18n.language ?? 'es')
            signin(auth, () => history.replace(from))
        }
    }

    const { isLoading, mutate: mutateLogin } = useMutation((payload) => fetcher(login(payload)), {
        onSuccess,
        onError: (error) => {
            toast.error(t(`errors.${error.message}`))
            console.log(error)
        }
    });

    const { mutate: mutateLoginToken } = useMutation((payload) => fetcher(respondChallenge(payload)), {
        onSuccess,
        onError: (error) => {
            toast.error(t(`errors.${error.message}`))
            if (error.message === 'TokenNotFound') {
                setToken(null);
            }
        }
    });


    const doSubmit = (formValues, actions) => {
        if (twoFactor) {
            mutateLoginToken({
                type: 'SOFTWARE_TOKEN_MFA',
                code: formValues.twofactorcode,
                session: twoFactor.session,
                username: twoFactor.username
            });
        } else {
            formValues.isSlack = isSlack;
            formValues.sessionId = sessionId;
            mutateLogin(formValues);
        }
    }

    const handleRecover = () => {
        history.push('/login/recover');
    }

    return (
        <Form className="text-center" onSubmit={handleSubmit(doSubmit)}>

            <Card className="p-4 mb-5 rounded-lg card-shadow">

                <span className="mb-5 font-weight-normal">{t('login.title')}</span>

                <FormGroup hidden={twoFactor}>
                    <Label
                        for="login-email"
                        className="sr-only">{t('login.email')}
                    </Label>
                    <Input
                        bsSize="md"
                        type="email"
                        name="email"
                        id="login-email"
                        innerRef={register({ required: true })}
                        placeholder={t('login.email_placeholder')}
                        autoFocus
                        invalid={errors['email']}
                    />
                    <FormFeedback invalid={errors['email']} >{t('login.missing')}</FormFeedback>
                </FormGroup>

                <FormGroup hidden={twoFactor}>
                    <Label
                        for="login-password"
                        className="sr-only">{t('login.password_placeholder')}
                    </Label>
                    <Input
                        bsSize="md"
                        type="password"
                        name="password"
                        id="login-password"
                        innerRef={register({ required: true })}
                        placeholder={t('login.password_placeholder')}
                        invalid={errors['password']}
                    />
                    <FormFeedback invalid={errors['password']}>{t('login.missing')}</FormFeedback>
                </FormGroup>
                {twoFactor &&
                    <FormGroup>
                        <Label
                            for="twofactorcode"
                            className="sr-only">{t('login.two_factor')}
                        </Label>
                        <Input
                            bsSize="md"
                            type="text"
                            name="twofactorcode"
                            id="twofactorcode"
                            innerRef={register({ required: true })}
                            placeholder={t('login.two_factor')}
                            required
                        />
                    </FormGroup>
                }
                <FormGroup hidden={isSlack} check className="mb-3">
                    <Label check size="sm">
                        <Input type="checkbox" />
                        {' '}{t('login.remember')}
                    </Label>
                </FormGroup>

                <Button color="primary" className="text-white font-weight-bold" type="submit" id="login-button">
                    {isLoading && <Spinner size="sm" color="light" />}{' '}{t('login.login')}
                </Button>

            </Card>

            <Button hidden={isSlack}  color="default" onClick={handleRecover} className="text-primary">{t('login.forgot')}</Button>

        </Form>
    )
}

export default function Login() {
    const { pathname } = useLocation();
    const isSlack = pathname === '/slack/oauth';
    
    return (
        <AuthLayout isSlack={isSlack}>
            <LoginForm isSlack={isSlack} />
        </AuthLayout>
    )
}
